import * as React from "react";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Order } from "../../helpers/TableHelpers";
import {DiscoveryCycleQueryParams, DiscoveryCyclesRepository} from "../../repositories/DiscoveryCyclesRepository";
import { DiscoveryCycle, DiscoveryCycleCensusState, hasCensusFigures } from "../../models/Statistics/DiscoveryCycle";
import {MigrationType} from "../../models/Statistics/MigrationType";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import {MigrationEventStepColors, MigrationEventStepIcons, MigrationEventStepLabels, MigrationEventStepType} from "../../models/Statistics/MigrationEventStepType";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import {convertUtcToLocal} from "../../helpers/CommonHelpers";
import { tableWrapperClassName } from "../common/Table/TableStyles";
import TablePagination from "../common/Table/TablePagination";
import TableEmptyState from "../common/TableEmptyState";
import CoverageBadge from "./CoverageBadge";
import DiscoveryCycleCensus from "./DiscoveryCycleCensus";

interface CasesTableProps {
  queryKey: string;
  migrationType: MigrationType
  organizationId: string;
  queryParams: DiscoveryCycleQueryParams;
  shouldFetchDiscoveryCycles: boolean;
  setShouldFetchDiscoveryCycles: React.Dispatch<React.SetStateAction<boolean>>
  onCountChange?: (count: number) => void;
}

const DiscoveryCycleTable: React.FC<CasesTableProps> = ({
  queryKey,
  migrationType,
  organizationId,
  queryParams,
  shouldFetchDiscoveryCycles,
  setShouldFetchDiscoveryCycles,
  onCountChange,
}) => {
  const discoveryCyclesRepository = new DiscoveryCyclesRepository();
  const [order, setOrder] = useState<Order>("desc");
  const [sort, setSort] = useState<string>("startDate");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [discoveryCycles, setDiscoveryCycles] = useState<DiscoveryCycle[]>([]);
  const [rows, setRows] = useState(0);
  const [expandedCycles, setExpandedCycles] = useState<Set<string>>(new Set());

  const toggleCensus = (cycleId: string) => {
    setExpandedCycles((current) => {
      const next = new Set(current);
      if (next.has(cycleId)) {
        next.delete(cycleId);
      } else {
        next.add(cycleId);
      }
      return next;
    });
  };

  const discoveryCyclesQuery = useQuery({ queryKey: [queryKey, organizationId, page, rowsPerPage, sort, order, queryParams, migrationType], queryFn:
    async () => {
      return await discoveryCyclesRepository.getDiscoveryCycles({
        migrationType: migrationType,
        params: queryParams,
        descending: order === "desc",
        orderBy: sort,
        pageNumber: page + 1,
        pageSize: rowsPerPage,
      });
    }, enabled: shouldFetchDiscoveryCycles,
      staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000 + 1,
      refetchIntervalInBackground: true,
      refetchOnMount: true, });
  const { isError, isFetching } = discoveryCyclesQuery;

  useEffect(() => {
    if (discoveryCyclesQuery.isSuccess && discoveryCyclesQuery.data?.data) {
      setDiscoveryCycles(discoveryCyclesQuery.data.data);
      const total = discoveryCyclesQuery.data.paging?.totalCount ?? 0;
      setRows(total);
      onCountChange?.(total);
      setShouldFetchDiscoveryCycles(false);
    }
    // dataUpdatedAt ticks on every successful fetch, even when the data payload
    // is structurally unchanged — this guarantees onCountChange fires after each
    // refetch so tab badges stay in sync with the latest query result.
  }, [discoveryCyclesQuery.dataUpdatedAt, discoveryCyclesQuery.isSuccess]);

  const changeSort = (newSort: string) => {
    setShouldFetchDiscoveryCycles(true);
    setPage(0);
    setSort(newSort);
  };

  const changeOrder = (newOrder: Order) => {
    setShouldFetchDiscoveryCycles(true);
    setPage(0);
    setOrder(newOrder);
  };

  const handleChangePage = (newPage: number) => {
    setShouldFetchDiscoveryCycles(true);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newValue: string) => {
    setShouldFetchDiscoveryCycles(true);
    setRowsPerPage(parseInt(newValue, 10));
    setPage(0);
  };

  return (
    <div className={cn(tableWrapperClassName, "max-h-[490px]", (isFetching || isError) && "opacity-50 pointer-events-none")}>
      <Table containerClassName="flex-1 min-h-0">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[1.5%]"><span className="sr-only">Cycle summary</span></TableHead>

            <TableHead className="w-[30%] px-1.5 py-3">
              <SortableTableColumnHeader
                label={"Client ID"}
                sortProperty={"createdBy"}
                getSort={() => sort}
                setSort={(newSort) => changeSort(newSort)}
                getOrder={() => order}
                setOrder={(newOrder) => changeOrder(newOrder)}
              />
            </TableHead>

            <TableHead className="w-[19%] px-1.5 py-3">
              <SortableTableColumnHeader
                label={"Event Status"}
                sortProperty={"eventStep"}
                getSort={() => sort}
                setSort={(newSort) => changeSort(newSort)}
                getOrder={() => order}
                setOrder={(newOrder) => changeOrder(newOrder)}
              />
            </TableHead>

            <TableHead className="w-[25%] px-1.5 py-3">
              <SortableTableColumnHeader
                label={"Start Date"}
                sortProperty={"startDate"}
                getSort={() => sort}
                setSort={(newSort) => changeSort(newSort)}
                getOrder={() => order}
                setOrder={(newOrder) => changeOrder(newOrder)}
              />
            </TableHead>

            <TableHead className="w-[25%] px-1.5 py-3">
              <SortableTableColumnHeader
                label={"End Date"}
                sortProperty={"endDate"}
                getSort={() => sort}
                setSort={(newSort) => changeSort(newSort)}
                getOrder={() => order}
                setOrder={(newOrder) => changeOrder(newOrder)}
              />
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {discoveryCycles.map((discoveryCycle) => {
            const expanded = expandedCycles.has(discoveryCycle.id);
            // Expandable for an unreachable root even with no census: roots are reported separately from links,
            // so a client that reports no link data can still have recorded an outage. Also expandable for a
            // withheld census, where the expander is the only route to why the figures are missing.
            const hasCensus =
              hasCensusFigures(discoveryCycle) ||
              discoveryCycle.rootsUnreachable > 0 ||
              discoveryCycle.censusState === DiscoveryCycleCensusState.Withheld;
            return (
            <React.Fragment key={discoveryCycle.id}>
            <TableRow>
              <TableCell className="px-1">
                {hasCensus && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 shrink-0"
                    onClick={() => toggleCensus(discoveryCycle.id)}
                    aria-label={expanded ? "Hide cycle summary" : "Show cycle summary"}
                    aria-expanded={expanded}
                  >
                    {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  </Button>
                )}
              </TableCell>
              <TableCell>{discoveryCycle.clientId}</TableCell>
              <TableCell>
                {/* The verdict and a withheld census are marked on the collapsed row on purpose. Both are
                    conditions that persist across every cycle a firm runs, so a badge reachable only by expanding
                    a row leaves a firm in that state indistinguishable at a glance from a healthy one. */}
                <div className="flex flex-wrap items-center gap-1.5">
                  {(() => {
                    const Icon = MigrationEventStepIcons[discoveryCycle.eventStep];
                    return (
                      <Badge variant={MigrationEventStepColors[discoveryCycle.eventStep]}>
                        <Icon className="size-3" />
                        {MigrationEventStepLabels[discoveryCycle.eventStep]}
                      </Badge>
                    );
                  })()}
                  <CoverageBadge
                    coverage={discoveryCycle.coverage}
                    degraded={discoveryCycle.degraded}
                    migrationType={migrationType}
                    rootsUnreachable={discoveryCycle.rootsUnreachable}
                  />
                  {discoveryCycle.censusState === DiscoveryCycleCensusState.Withheld && (
                    <Badge variant="danger">
                      <AlertTriangle className="size-3" />
                      No totals
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>{convertUtcToLocal(discoveryCycle.startDate)}</TableCell>

              {
                discoveryCycle.eventStep != MigrationEventStepType.Started ? (
                  <TableCell>{convertUtcToLocal(discoveryCycle.endDate)}</TableCell>
                ) : (
                  <TableCell>-</TableCell>
                )
              }

            </TableRow>
            {expanded && (
              <TableRow className="hover:bg-transparent">
                <TableCell />
                <TableCell colSpan={98} className="bg-muted/30 py-3">
                  <DiscoveryCycleCensus cycle={discoveryCycle} organizationId={organizationId} migrationType={migrationType} />
                </TableCell>
              </TableRow>
            )}
            </React.Fragment>
            );
          })}
        {discoveryCycles.length === 0 && !isFetching && (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={99}>
              <TableEmptyState title="No discovery cycles found" />
            </TableCell>
          </TableRow>
        )}
        </TableBody>

      </Table>
      <TablePagination
        page={page}
        rowsPerPage={rowsPerPage}
        rows={rows}
        onPageChange={(newPage) => { handleChangePage(newPage); }}
        onRowsPerPageChange={(val) => { handleChangeRowsPerPage(String(val)); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </div>
  );
};

export default DiscoveryCycleTable;
